/**
 * BotAvatarUpload — circular preview with upload overlay,
 * matching the personal-info-section.tsx pattern.
 */

import { Camera01 } from '@untitledui/icons';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useOrg } from '@/contexts/OrgContext';
import { fileService } from '@/lib/files/service';

const AVATAR_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';
const AVATAR_MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB

interface BotAvatarUploadProps {
  currentUrl: string;
  onUploaded: (url: string) => void;
  onRemoved: () => void;
}

export function BotAvatarUpload({
  currentUrl,
  onUploaded,
  onRemoved,
}: BotAvatarUploadProps) {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const [uploading, setUploading] = useState(false);
  const [imgError, setImgError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !orgId) {
        return;
      }
      // Reset so re-selecting the same file triggers onChange
      e.target.value = '';

      if (file.size > AVATAR_MAX_SIZE_BYTES) {
        toast.error('Image must be under 2 MB');
        return;
      }

      setUploading(true);
      setImgError(false);
      try {
        const uploaded = await fileService.uploadFile(orgId, file);
        const url = uploaded.file_url ?? uploaded.download_url ?? '';
        if (!url) {
          throw new Error('No URL returned from upload');
        }
        onUploaded(url);
        toast.success('Avatar uploaded');
      } catch {
        toast.error("We couldn't upload the avatar. Try again.");
      } finally {
        setUploading(false);
      }
    },
    [orgId, onUploaded]
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImgError = useCallback(() => {
    setImgError(true);
  }, []);

  const handleRemove = useCallback(() => {
    setImgError(false);
    onRemoved();
  }, [onRemoved]);

  const hasAvatar = currentUrl.length > 0 && !imgError;

  // Contextual hint below the circle: format info, uploading state, or remove action.
  // Using if/else to avoid nested ternary (ESLint no-nested-ternary).
  let avatarHint: React.ReactNode;
  if (uploading) {
    avatarHint = <p className='text-[11px] text-fg-quaternary'>Uploading…</p>;
  } else if (hasAvatar) {
    avatarHint = (
      <button
        type='button'
        onClick={handleRemove}
        className='text-xs text-utility-error-600 hover:underline'
      >
        Remove
      </button>
    );
  } else {
    avatarHint = (
      <p className='text-[11px] text-fg-quaternary text-center leading-tight'>
        PNG, JPG, GIF
        <br />
        or WebP · Max 2 MB
      </p>
    );
  }

  return (
    <div className='flex flex-col items-center gap-1.5'>
      {/* Avatar circle — the entire surface is a click target */}
      <button
        type='button'
        onClick={handleClick}
        disabled={uploading}
        className='group relative w-20 h-20 rounded-full overflow-hidden border-2 border-border-secondary focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-v2-primary)] disabled:opacity-50 disabled:cursor-not-allowed'
        aria-label='Upload bot avatar'
      >
        {hasAvatar ? (
          <img
            src={currentUrl}
            alt='Bot avatar'
            className='w-full h-full object-cover'
            onError={handleImgError}
          />
        ) : (
          <div className='w-full h-full bg-bg-secondary flex items-center justify-center'>
            <Camera01 className='w-6 h-6 text-fg-quaternary' />
          </div>
        )}
        {/* Hover overlay */}
        <div className='absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center'>
          <Camera01 className='w-5 h-5 text-white' />
        </div>
      </button>
      <input
        ref={fileInputRef}
        type='file'
        accept={AVATAR_ACCEPT}
        onChange={handleFileSelect}
        disabled={uploading}
        className='hidden'
        aria-hidden='true'
      />

      {/* Contextual hint */}
      {avatarHint}
    </div>
  );
}
